/**
 * FitnessNutritionTracking — متابعة الوجبات الغذائية
 * Feed of all meal logs uploaded by members + assigned nutritionist/coach.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Apple, Loader2, Search, RefreshCw, User, Calendar, Plus, Trash2, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import FitnessLogModal from '../components/FitnessLogModal';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NUTRITION_FIELDS = [
  { key: 'user_name',  labelAr: 'اسم العضو',  labelEn: 'Member Name', type: 'autocomplete', required: true, placeholderAr: 'اختر أو اكتب الاسم', placeholderEn: 'Pick or type name' },
  { key: 'meal_name',  labelAr: 'اسم الوجبة', labelEn: 'Meal Name',   type: 'text', placeholderAr: 'مثال: صدر دجاج مشوي', placeholderEn: 'e.g. Grilled chicken' },
  { key: 'meal_type',  labelAr: 'نوع الوجبة', labelEn: 'Meal Type',   type: 'select', options: [
      { value: 'breakfast', ar: 'الإفطار', en: 'Breakfast' },
      { value: 'lunch',     ar: 'الغداء',  en: 'Lunch' },
      { value: 'dinner',    ar: 'العشاء',  en: 'Dinner' },
      { value: 'snack',     ar: 'وجبة خفيفة', en: 'Snack' },
    ] },
  { key: 'calories', labelAr: 'السعرات الحرارية', labelEn: 'Calories', type: 'number' },
  { key: 'protein',  labelAr: 'البروتين (جم)',  labelEn: 'Protein (g)', type: 'number', step: '0.1' },
  { key: 'carbs',    labelAr: 'الكربوهيدرات (جم)', labelEn: 'Carbs (g)', type: 'number', step: '0.1' },
  { key: 'fat',      labelAr: 'الدهون (جم)',    labelEn: 'Fat (g)',     type: 'number', step: '0.1' },
  { key: 'notes',    labelAr: 'ملاحظات',         labelEn: 'Notes', type: 'textarea', fullWidth: true },
];

export default function FitnessNutritionTracking() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fitness/nutrition-logs`, { headers: getAuthHeaders() });
      setLogs(res.data?.data || res.data?.logs || []);
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSave = async (values) => {
    if (!values.user_name?.trim()) {
      toast.error(language === 'ar' ? 'اسم العضو مطلوب' : 'Member name required');
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await axios.put(`${API_URL}/api/fitness/nutrition-logs/${editTarget.id}`, values, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم تحديث الوجبة' : 'Meal updated');
      } else {
        await axios.post(`${API_URL}/api/fitness/nutrition-logs`, values, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم إضافة الوجبة' : 'Meal added');
      }
      closeModal();
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الحفظ' : 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (log) => { setEditTarget(log); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'حذف هذه الوجبة؟' : 'Delete this meal?')) return;
    try {
      await axios.delete(`${API_URL}/api/fitness/nutrition-logs/${id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const visible = logs.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [l.user_name, l.coach_name, l.meal_name]
      .some((v) => v && String(v).toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title="متابعة الوجبات الغذائية"
      titleEn="Meals Tracking"
      subtitle="جميع الوجبات الجديدة المرفوعة من الأعضاء"
      subtitleEn="All new meals uploaded by members"
      screenCode="FIT-003"
      backPath="/dashboard/606-fitness"
      onBack={() => navigate('/dashboard/606-fitness')}
      breadcrumb={[
        { labelAr: '606 FITNESS', labelEn: '606 FITNESS', path: '/dashboard/606-fitness' },
        { labelAr: 'متابعة الوجبات الغذائية', labelEn: 'Meals Tracking' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="fitness-nutrition-page">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'ar' ? 'بحث بالعضو/المدرب/الوجبة...' : 'Search by member/coach/meal...'}
              className="w-full bg-white/[0.02] border border-white/10 rounded-xl py-3 px-10 text-white placeholder-[#71717A] focus:outline-none focus:border-emerald-500/50"
              data-testid="nutrition-search"
            />
          </div>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 hover:bg-emerald-500/20 transition-colors"
            data-testid="nutrition-refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openAdd}
            className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
            data-testid="nutrition-add-btn"
          >
            <Plus className="h-5 w-5" />
            {language === 'ar' ? 'إضافة وجبة' : 'Add Meal'}
          </button>
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#A1A1AA]">
              <Apple className="h-14 w-14 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا توجد وجبات حديثة' : 'No recent meals'}</p>
              <p className="text-xs text-[#606060] mt-2">
                {language === 'ar' ? 'ستظهر هنا كل وجبة جديدة يضيفها عضو أو مدرب' : 'New meals will appear here as members/coaches add them'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {visible.map((log) => (
                <div key={log.id || `${log.user_id}-${log.created_at}`} className="p-4 hover:bg-white/[0.02]" data-testid={`nutrition-row-${log.id || log.user_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center">
                        <Apple className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{log.meal_name || log.type || (language === 'ar' ? 'وجبة' : 'Meal')}</p>
                        <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{log.user_name || '—'}</span>
                          {log.coach_name && (
                            <span className="text-emerald-400">{language === 'ar' ? 'المدرب:' : 'Coach:'} {log.coach_name}</span>
                          )}
                          {log.created_at && (
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(log.created_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(log.calories || log.protein) && (
                      <div className="flex items-center gap-3 text-xs text-[#A1A1AA]">
                        {log.calories && <span>{log.calories} {language === 'ar' ? 'سعرة' : 'kcal'}</span>}
                        {log.protein && <span>{log.protein}g {language === 'ar' ? 'بروتين' : 'protein'}</span>}
                      </div>
                    )}
                    {log.id && (
                      <>
                        <button
                          onClick={() => openEdit(log)}
                          className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-amber-400 hover:border-amber-500/30 transition-colors"
                          data-testid={`nutrition-edit-${log.id}`}
                          title={language === 'ar' ? 'تعديل' : 'Edit'}
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(log.id)}
                          className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-red-400 hover:border-red-500/30 transition-colors"
                          data-testid={`nutrition-delete-${log.id}`}
                          title={language === 'ar' ? 'حذف' : 'Delete'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <FitnessLogModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSave}
        title="إضافة وجبة جديدة"
        titleEn="Add New Meal"
        fields={NUTRITION_FIELDS}
        saving={saving}
        color="amber"
        initialValues={editTarget}
      />
    </DashboardLayout>
  );
}
